import { useState, useMemo } from 'react'
import styled from 'styled-components'
import {
    AreaChart,
    Area,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    ReferenceLine,
} from 'recharts'

interface WeeklyLoadEntry {
    week_start: string
    avg_load_pct: number
    is_projected?: boolean
}

interface LoadHistoryChartProps {
    data: WeeklyLoadEntry[]
    onRangeChange?: (months: number) => void
}

const RANGE_OPTIONS = [
    { label: '1M', months: 1 },
    { label: '3M', months: 3 },
    { label: '6M', months: 6 },
    { label: '1A', months: 12 },
]

const ChartContainer = styled.div`
    background: white;
    border-radius: 8px;
    padding: 16px;
    margin-top: 12px;
`

const ChartHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
`

const ChartTitle = styled.h4`
    margin: 0;
    font-size: 14px;
    color: #37474f;
`

const RangeButtons = styled.div`
    display: flex;
    gap: 4px;
`

const RangeBtn = styled.button<{ $active: boolean }>`
    padding: 3px 10px;
    border: 1px solid ${({ $active }) => ($active ? '#1976D2' : '#ccc')};
    background: ${({ $active }) => ($active ? '#1976D2' : 'white')};
    color: ${({ $active }) => ($active ? 'white' : '#666')};
    border-radius: 4px;
    font-size: 11px;
    cursor: pointer;
`

const formatWeek = (dateStr: string) => {
    const d = new Date(dateStr)
    const day = d.getDate()
    const months = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic']
    return `${day} ${months[d.getMonth()]}`
}

export default function LoadHistoryChart({ data, onRangeChange }: LoadHistoryChartProps) {
    const [range, setRange] = useState(6)

    const chartData = useMemo(() => {
        const todayStr = new Date().toISOString().split('T')[0]
        return data.map((e, idx) => {
            const isProj = e.is_projected === true
            const load = Math.round(e.avg_load_pct)
            const isTransition = !isProj && idx < data.length - 1 && data[idx + 1]?.is_projected === true
            return {
                week: formatWeek(e.week_start),
                load: !isProj ? load : null,
                load_projected: isProj || isTransition || e.week_start >= todayStr ? load : null,
                raw: e.week_start,
            }
        })
    }, [data])

    const handleRange = (months: number) => {
        setRange(months)
        onRangeChange?.(months)
    }

    if (!chartData.length) return null

    return (
        <ChartContainer>
            <ChartHeader>
                <ChartTitle>Carga semanal (historial + proyección)</ChartTitle>
                <RangeButtons>
                    {RANGE_OPTIONS.map((opt) => (
                        <RangeBtn key={opt.months} $active={range === opt.months} onClick={() => handleRange(opt.months)}>
                            {opt.label}
                        </RangeBtn>
                    ))}
                </RangeButtons>
            </ChartHeader>
            <ResponsiveContainer width="100%" height={220}>
                <AreaChart data={chartData} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
                    <defs>
                        <linearGradient id="loadGradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#1976D2" stopOpacity={0.3} />
                            <stop offset="95%" stopColor="#1976D2" stopOpacity={0.05} />
                        </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis
                        dataKey="week"
                        tick={{ fontSize: 10 }}
                        interval={Math.max(0, Math.floor(chartData.length / 8) - 1)}
                    />
                    <YAxis tick={{ fontSize: 11 }} unit="%" />
                    <Tooltip
                        formatter={(value) => [`${value}%`, 'Carga']}
                        labelFormatter={(label) => `Semana del ${label}`}
                        contentStyle={{ fontSize: 12 }}
                    />
                    <ReferenceLine
                        y={100}
                        stroke="#F44336"
                        strokeDasharray="4 4"
                        label={{ value: '100%', position: 'right', fontSize: 10, fill: '#F44336' }}
                    />
                    <Area
                        type="monotone"
                        dataKey="load"
                        stroke="#1976D2"
                        strokeWidth={2}
                        fill="url(#loadGradient)"
                        dot={chartData.length <= 13}
                        connectNulls={false}
                        name="Carga"
                    />
                    <Area
                        type="monotone"
                        dataKey="load_projected"
                        stroke="#1976D2"
                        strokeWidth={2}
                        strokeDasharray="8 4"
                        fill="url(#loadGradient)"
                        fillOpacity={0.3}
                        dot={false}
                        connectNulls={false}
                        name="Proyección"
                    />
                </AreaChart>
            </ResponsiveContainer>
        </ChartContainer>
    )
}
